import React, { useMemo } from 'react';
import { Flex, TextV2 } from '@withjoy/joykit';
import type { HotelRoomBlockPricingValues, StandardHotelPricingValues, AccommodationsConsoleState } from '@shared/types/Accommodations.types';
import { Skeleton } from '@shared/components/Skeleton';
import { useTranslation } from '@shared/core';
import { useDiscountBadge } from './useDiscountBadge';
import { useAccommodationsListingMobileLayout } from '@apps/stays/AccommodationsConsole/hooks/useAccommodationsListingMobileLayout';
import { hasOnsiteChargesValue, pickDisplayTotalString, useIsAllInTotalEnabled } from '@shared/hooks/useAllInTotal/useAllInTotal';

const CostBreakdown = ({
  pricingInfo,
  noOfNights,
  occupancies,
  alignment = 'end',
  hideDiscountBadge = false,
  inlineTotal = false,
}: Readonly<{
  pricingInfo: StandardHotelPricingValues | HotelRoomBlockPricingValues;
  occupancies: AccommodationsConsoleState['occupancies'];
  noOfNights: number;
  /** `end` (default) is the right-hand price column every tile ships; `start` is the cash-fund
   *  card, whose comp runs the price row down the left edge under the address. */
  alignment?: 'start' | 'end';
  /** Let the caller place the `% off` badge itself. The cash-fund comp puts it up on the stars row,
   *  which this component cannot reach. */
  hideDiscountBadge?: boolean;
  /** Run the total on the `/ night` line after a `·`, instead of on its own line beneath it — the
   *  cash-fund comp's one-line price row. The nights/rooms disclosure still follows below it. */
  inlineTotal?: boolean;
}>) => {
  const { isMobileOrTablet } = useAccommodationsListingMobileLayout();
  // The discount figures are read by `useDiscountBadge`, which owns the badge and its tooltip.
  const { nightlyBaseRateString, strikeoutNightlyBaseRateString, totalRateString, allInTotalRateString, onsiteChargesTotalRate } = pricingInfo;

  // Same arm `useListingSort` orders by, so the totals below read in the order the list is in.
  const isAllInTotalEnabled = useIsAllInTotalEnabled();
  // The string/major-unit variants of the shared selectors: this surface is handed formatted totals
  // and already-divided numbers, never a `DisplayMonetaryValue`, but the rules must stay the ones in
  // `allInTotal.ts` — in particular that an absent charge is "not determined", never zero.
  const displayTotalString = pickDisplayTotalString(totalRateString, allInTotalRateString, isAllInTotalEnabled);
  const includesPropertyCharges = isAllInTotalEnabled && hasOnsiteChargesValue(onsiteChargesTotalRate);

  const { tq } = useTranslation('accommodationsConsole');
  // Skipped when the caller places the badge itself: reading the flag twice for one listing would
  // bank two Amplitude exposures, and the queue does not dedupe.
  const discountBadge = useDiscountBadge(pricingInfo, undefined, { skip: hideDiscountBadge });

  // The nights/rooms matrix is branched once and the arm chosen at each leaf. Each leaf is still a
  // whole sentence rather than a stem plus an interchangeable suffix: `tq` needs a complete literal
  // for the extractor to pick up, and a clause like "incl. taxes and fees" cannot be translated
  // independently of what precedes it in languages that inflect for case.
  const occupancyText = useMemo(() => {
    const unitCount = occupancies.unitCount;
    if (noOfNights === 1) {
      if (unitCount === 1) {
        return includesPropertyCharges ? tq`1 night, 1 room, incl. taxes, fees & property charges` : tq`1 night, 1 room, incl. taxes and fees`;
      }
      return includesPropertyCharges ? tq`1 night, ${unitCount} rooms, incl. taxes, fees & property charges` : tq`1 night, ${unitCount} rooms, incl. taxes and fees`;
    }
    if (unitCount === 1) {
      return includesPropertyCharges ? tq`${noOfNights} nights, 1 room, incl. taxes, fees & property charges` : tq`${noOfNights} nights, 1 room, incl. taxes and fees`;
    }
    return includesPropertyCharges
      ? tq`${noOfNights} nights, ${unitCount} rooms, incl. taxes, fees & property charges`
      : tq`${noOfNights} nights, ${unitCount} rooms, incl. taxes and fees`;
  }, [tq, noOfNights, occupancies.unitCount, includesPropertyCharges]);

  const isStartAligned = alignment === 'start';
  const textAlign = isStartAligned ? 'start' : 'end';

  return (
    <Flex
      flexDirection="column"
      alignItems={isStartAligned ? 'flex-start' : 'flex-end'}
    >
      {!hideDiscountBadge && discountBadge}
      <Flex
        columnGap={1}
        alignItems="center"
        flexWrap="wrap"
        justifyContent={isStartAligned ? 'flex-start' : 'flex-end'}
        marginTop={!hideDiscountBadge && discountBadge ? 2 : 0}
      >
        {strikeoutNightlyBaseRateString && (
          <TextV2
            textDecoration="line-through"
            color="mono10"
            typographyVariant="label2"
          >
            {strikeoutNightlyBaseRateString}
          </TextV2>
        )}
        <TextV2
          typographyVariant={isMobileOrTablet ? 'hed1' : 'hed6'}
          fontWeight={600}
        >
          {nightlyBaseRateString}
        </TextV2>
        <TextV2
          typographyVariant={isMobileOrTablet ? 'label1' : 'label2'}
          color="mono10"
          fontWeight={400}
        >
          {tq`/ night`}
        </TextV2>
        {/* Same string as the standalone line below, so the total reads identically either way. */}
        {inlineTotal && displayTotalString && (
          <TextV2
            typographyVariant={isMobileOrTablet ? 'label1' : 'label2'}
            color="mono10"
            fontWeight={400}
            whiteSpace="nowrap"
          >
            {' · '}
            {tq`${displayTotalString} total`}
          </TextV2>
        )}
      </Flex>
      {displayTotalString && (
        <>
          {!inlineTotal && (
            <TextV2
              marginTop={1}
              textAlign={textAlign}
              typographyVariant={isMobileOrTablet ? 'label1' : 'label2'}
              color="mono10"
            >
              {tq`${displayTotalString} total`}
            </TextV2>
          )}
          {/* Kept even when the total moves inline: this is the taxes/fees/property-charges
              disclosure, not decoration, so it is not something a layout change may drop. */}
          <TextV2
            marginTop={1}
            textAlign={textAlign}
            typographyVariant="label1"
            color="mono10"
          >
            {occupancyText}
          </TextV2>
        </>
      )}
    </Flex>
  );
};

// `hideCta` is for tiles that moved the button out of the price column — the redesigned fund tile
// carries it in its footer strip instead, so a bar here would promise a second button. `alignment`
// has to track the tile's own, or the bars sit on the opposite edge from the prices that replace them.
CostBreakdown.Skeleton = ({ hideCta, alignment = 'end' }: { hideCta?: boolean; alignment?: 'start' | 'end' }) => (
  <Flex
    rowGap="10px"
    alignItems={alignment === 'start' ? 'flex-start' : 'flex-end'}
    flexDirection="column"
  >
    <Skeleton
      height={28}
      width={105}
    />
    <Skeleton
      height={16}
      width={80}
    />
    <Skeleton
      height={12}
      width={210}
    />
    {!hideCta && (
      <Flex
        width="100%"
        marginTop={5}
        justifyContent="center"
      >
        <Skeleton
          height={30}
          width={{ _: '100%', md: 200 }}
        />
      </Flex>
    )}
  </Flex>
);

export { CostBreakdown };
